/**
     * NoCodeBackend API Service for ILM Global Quotes Management
     * Handles all API interactions with the NoCodeBackend database
     */

    const NOCODEBACKEND_API_KEY = '4329efd70573398a463a2d935b67bbc1122496aff9fa02eae2d92d50bcb0';
    const NOCODEBACKEND_BASE_URL = 'https://api.nocodebackend.com/api';

    /**
     * Generic API request handler
     * @param {string} endpoint - API endpoint
     * @param {object} options - Fetch options
     * @returns {Promise<any>} - API response data
     */
    async function apiRequest(endpoint, options = {}) {
      try {
        const response = await fetch(`${NOCODEBACKEND_BASE_URL}${endpoint}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${NOCODEBACKEND_API_KEY}`,
            ...options.headers,
          },
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error('NoCodeBackend API Error:', error);
        throw error;
      }
    }

    /**
     * Get all quote categories
     * @returns {Promise<Array>} - List of categories
     */
    export async function getQuoteCategories() {
      try {
        const data = await apiRequest('/quotes_categories');
        return data.data || data || [];
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Return fallback data
        return [
          {
            id: '1',
            name: 'Educational',
            description: 'Quotes about learning, teaching, and personal development',
            icon: 'FiBookOpen',
            color: '#3B82F6',
            is_active: true,
            sort_order: 1
          },
          {
            id: '2',
            name: 'Leadership',
            description: 'Insights on leading, guiding, and inspiring others',
            icon: 'FiTarget',
            color: '#10B981',
            is_active: true,
            sort_order: 2
          },
          {
            id: '3',
            name: 'Inspirational',
            description: 'Motivational quotes to inspire and uplift',
            icon: 'FiSun',
            color: '#F59E0B',
            is_active: true,
            sort_order: 3
          }
        ];
      }
    }

    /**
     * Get topics by category ID
     * @param {string} categoryId - Category ID
     * @returns {Promise<Array>} - List of topics
     */
    export async function getTopicsByCategory(categoryId) {
      try {
        const data = await apiRequest(`/quotes_topics?category_id=${categoryId}`);
        return data.data || data || [];
      } catch (error) {
        console.error('Error fetching topics:', error);
        return [];
      }
    }

    /**
     * Get all topics
     * @returns {Promise<Array>} - List of all topics
     */
    export async function getAllTopics() {
      try {
        const data = await apiRequest('/quotes_topics');
        return data.data || data || [];
      } catch (error) {
        console.error('Error fetching all topics:', error);
        return [];
      }
    }

    /**
     * Get quotes by topic ID
     * @param {string} topicId - Topic ID
     * @returns {Promise<Array>} - List of quotes
     */
    export async function getQuotesByTopic(topicId) {
      try {
        const data = await apiRequest(`/quotes_collection?topic_id=${topicId}&is_active=true`);
        return data.data || data || [];
      } catch (error) {
        console.error('Error fetching quotes:', error);
        return [];
      }
    }

    /**
     * Get random quotes
     * @param {number} limit - Number of quotes to retrieve
     * @returns {Promise<Array>} - List of random quotes
     */
    export async function getRandomQuotes(limit = 10) {
      try {
        const data = await apiRequest(`/quotes_collection?is_active=true&limit=${limit}&random=true`);
        return data.data || data || [];
      } catch (error) {
        console.error('Error fetching random quotes:', error);
        return [];
      }
    }

    /**
     * Create a new quote (Admin only)
     * @param {object} quoteData - Quote data
     * @returns {Promise<object>} - Created quote
     */
    export async function createQuote(quoteData) {
      try {
        const data = await apiRequest('/quotes_collection', {
          method: 'POST',
          body: JSON.stringify(quoteData),
        });
        return data.data || data;
      } catch (error) {
        console.error('Error creating quote:', error);
        throw error;
      }
    }

    /**
     * Update a quote (Admin only)
     * @param {string} id - Quote ID
     * @param {object} updates - Quote updates
     * @returns {Promise<object>} - Updated quote
     */
    export async function updateQuote(id, updates) {
      try {
        const data = await apiRequest(`/quotes_collection/${id}`, {
          method: 'PUT',
          body: JSON.stringify(updates),
        });
        return data.data || data;
      } catch (error) {
        console.error('Error updating quote:', error);
        throw error;
      }
    }

    /**
     * Delete a quote (Admin only)
     * @param {string} id - Quote ID
     * @returns {Promise<boolean>} - Success status
     */
    export async function deleteQuote(id) {
      try {
        await apiRequest(`/quotes_collection/${id}`, {
          method: 'DELETE',
        });
        return true;
      } catch (error) {
        console.error('Error deleting quote:', error);
        throw error;
      }
    }

    /**
     * Create a new category (Admin only)
     * @param {object} categoryData - Category data
     * @returns {Promise<object>} - Created category
     */
    export async function createCategory(categoryData) {
      try {
        const data = await apiRequest('/quotes_categories', {
          method: 'POST',
          body: JSON.stringify(categoryData),
        });
        return data.data || data;
      } catch (error) {
        console.error('Error creating category:', error);
        throw error;
      }
    }

    /**
     * Create a new topic (Admin only)
     * @param {object} topicData - Topic data
     * @returns {Promise<object>} - Created topic
     */
    export async function createTopic(topicData) {
      try {
        const data = await apiRequest('/quotes_topics', {
          method: 'POST',
          body: JSON.stringify(topicData),
        });
        return data.data || data;
      } catch (error) {
        console.error('Error creating topic:', error);
        throw error;
      }
    }

    /**
     * Update quote likes count
     * @param {string} id - Quote ID
     * @param {number} increment - Number to increment by (1 or -1)
     * @returns {Promise<object>} - Updated quote
     */
    export async function updateQuoteLikes(id, increment) {
      try {
        const data = await apiRequest(`/quotes_collection/${id}/likes`, {
          method: 'PATCH',
          body: JSON.stringify({ increment }),
        });
        return data.data || data;
      } catch (error) {
        console.error('Error updating quote likes:', error);
        throw error;
      }
    }

    /**
     * Search quotes
     * @param {string} query - Search query
     * @returns {Promise<Array>} - List of matching quotes
     */
    export async function searchQuotes(query) {
      try {
        const data = await apiRequest(`/quotes_collection?search=${encodeURIComponent(query)}&is_active=true`);
        return data.data || data || [];
      } catch (error) {
        console.error('Error searching quotes:', error);
        return [];
      }
    }

    /**
     * Get quotes by category
     * @param {string} categoryId - Category ID
     * @returns {Promise<Array>} - List of quotes
     */
    export async function getQuotesByCategory(categoryId) {
      try {
        const data = await apiRequest(`/quotes_collection?category_id=${categoryId}&is_active=true`);
        return data.data || data || [];
      } catch (error) {
        console.error('Error fetching quotes by category:', error);
        return [];
      }
    }

    export default {
      getQuoteCategories,
      getTopicsByCategory,
      getAllTopics,
      getQuotesByTopic,
      getRandomQuotes,
      createQuote,
      updateQuote,
      deleteQuote,
      createCategory,
      createTopic,
      updateQuoteLikes,
      searchQuotes,
      getQuotesByCategory,
    };